import { CliError, CliErrorCode } from '@wix/cli-error';
import type { UserHooksHandlers, HookPayloadsMap } from './hooks.js';
import { USER_HOOK_NAMES } from './hooks.js';
import type { WixCliPluginShape } from '../types/wix-astro-config.js';

const MIN_PLUGIN_NAME_LENGTH = 3;

export const normalizePluginName = (name: string): string => {
  return name.trim().toLowerCase();
};

function throwInvalidConfigDefinition(reason: string): never {
  throw new CliError({
    code: CliErrorCode.InvalidConfigDefinition({ reason }),
    cause: null,
  });
}

function assertRecord(
  value: unknown,
  reason: string
): asserts value is Record<string, unknown> {
  if (typeof value !== 'object' || value == null || Array.isArray(value)) {
    throwInvalidConfigDefinition(reason);
  }
}

export function assertPluginsProp(
  plugins?: unknown
): asserts plugins is unknown[] | undefined {
  if (plugins !== undefined && !Array.isArray(plugins)) {
    throwInvalidConfigDefinition('`plugins` must be an array.');
  }
}

export function assertPluginName(
  plugin: unknown,
  index: number
): asserts plugin is { name: string; hooks?: unknown } {
  assertRecord(plugin, `"plugins[${index}]" must be an object.`);

  if (
    typeof plugin.name !== 'string' ||
    normalizePluginName(plugin.name).length < MIN_PLUGIN_NAME_LENGTH
  ) {
    throwInvalidConfigDefinition(
      `"plugins[${index}].name" must be a string at least ${MIN_PLUGIN_NAME_LENGTH} characters long.`
    );
  }
}

export function assertPluginHooksShape(
  plugin: { hooks?: unknown },
  name: string
): asserts plugin is WixCliPluginShape {
  if (plugin.hooks !== undefined) {
    assertRecord(
      plugin.hooks,
      `"hooks" property in plugin "${name}" must be an object.`
    );
  }
}

function assertUserHookName(
  key: string,
  pluginName: string
): asserts key is keyof HookPayloadsMap {
  if (!USER_HOOK_NAMES.includes(key as (typeof USER_HOOK_NAMES)[number])) {
    throw new CliError({
      code: CliErrorCode.PluginUnknownHook({
        hook: key,
        supportedHooks: [...USER_HOOK_NAMES],
        pluginName,
      }),
      cause: null,
    });
  }
}

type UserHookEntry = {
  [K in keyof HookPayloadsMap]: [
    hook: K,
    handler: NonNullable<UserHooksHandlers[K]>,
  ];
}[keyof HookPayloadsMap];

export function assertUserHookHandler(
  entry: [hook: string, handler: unknown],
  pluginName: string
): asserts entry is UserHookEntry {
  const [key, value] = entry;

  assertUserHookName(key, pluginName);

  if (typeof value !== 'function') {
    throw new CliError({
      code: CliErrorCode.PluginInvalidHookHandler({ hook: key, pluginName }),
      cause: null,
    });
  }
}
